import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import axios from 'axios';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Tooltip } from '@material-ui/core';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { AutocompleteSearchIcon } from '../UI/icons';
import { InputAdornment, InputBase } from '@material-ui/core';
import debounce from 'lodash/debounce';
import {
    removeMarker,
    setMarkers
} from '../../redux/markers/Actions';
import {
    setFitBounds
} from '../../redux/map/Actions';
import { MAIN_MAP } from '../../redux/map/Constants';
import { setHighlightedFeatures } from '../../redux/highlights/Actions';


const AutocompleteSearch = ({
    classes,
    dispatchSetFitBounds,
    dispatchSetMarkers,
    dispatchRemoveMarker,
    dispatchSetHighlightedFeatures,
    map
}) => {
    const [options, setOptions] = useState([]);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState(null);

    const getAutocompleteOptions = debounce((inputValue) => {
        axios.get('/api/search/autocomplete', {
            params: {
                address: inputValue
            }
        }).then((res) => {
            const predictions = res.data.predictions.map(p => ({
                id: p.place_id,
                address: p.description
            }));
            setOptions(predictions);
        })
            .catch((err) => {
                console.log(err);
                setOptions([]);
            });
    }, 500, {
        leading: true,
        trailing: true
    });

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            goTo();
        }
    };

    const goTo = () => {
        const params = value ? { place_id: value.id } : { value: inputValue };
        setOptions([]);

        axios.get('/api/search/details', {
            params: params
        }).then((res) => {
            if (res.data.bounds) {
                dispatchSetFitBounds(res.data.bounds, { maxZoom: 16 });
            } else if (res.data.centroid) {
                dispatchSetFitBounds([res.data.centroid, res.data.centroid], { maxZoom: 16 });
            }
            if (res.data.location) {
                dispatchSetMarkers([
                    {
                        id: 1,
                        name: res.data.label,
                        location: res.data.location
                    }
                ]);
            }
            switch (res.data.type) {
                case 'google':
                    break;
                case 'hmlr_poly': {
                    const highlightedFeatures = [];
                    for (const id of res.data.ids) {
                        const feature = {
                            featureId: id
                        };
                        const layerSystemName = 'hmlr_land_parcels';
                        let highlightedLayer = highlightedFeatures.find(e => e.layerSystemName === layerSystemName);
                        if (!highlightedLayer) {
                            highlightedLayer = {
                                key: `${layerSystemName}_search_selected`,
                                layerSystemName: layerSystemName,
                                features: []
                            };
                            highlightedFeatures.push(highlightedLayer);
                        }
                        highlightedLayer.features.push(feature);
                    }
                    dispatchSetHighlightedFeatures(highlightedFeatures);
                    break;
                }
                case 'planning_application': {
                    const highlightedFeatures = [];
                    const feature = {
                        featureId: res.data.label,
                        lngLat: res.data.centroid
                    };
                    const layerSystemName = 'planning_applications';
                    const highlightedLayer = {
                        key: `${layerSystemName}_search_selected`,
                        layerSystemName: layerSystemName,
                        features: [feature]
                    };
                    highlightedFeatures.push(highlightedLayer);
                    dispatchSetHighlightedFeatures(highlightedFeatures);
                    break;
                }
                case 'latlng':
                    break;
                default:
                    console.error('Not a valid type');
            }
            if (res.data.label) {
                setInputValue(res.data.label);
            }
        }).catch((err) => {
            console.error(err);
        });
    };

    const setHighlightValue = (e, value) => {
        setValue(value);
    };

    useEffect(() => {
        if (inputValue === null || inputValue.length === 0) {
            setOptions([])
            setValue(null);
        } else {
            getAutocompleteOptions(inputValue);
        }
    }, [inputValue]);

    const [showTooltip, setShowTooltip] = useState(false);

    const handleAutocompleteFocus = () => {
        setShowTooltip(false);
    };

    const handleTooltipHover = () => {
        setShowTooltip(true);
    };

    const handleTooltipHoverLeave = () => {
        setShowTooltip(false);
    };

    return (
        <Tooltip 
        title="You can search for a known address, Post Code, HMLR Title Deed number, Planning Application Reference Number and Latitude & Longitude coordinates"
        placement='bottom'
        disableFocusListener 
        disableTouchListener
        open={showTooltip}
        classes={{ tooltip: classes.customWidth }}
        // onMouseEnter={handleTooltipHover}
        // onMouseOut={handleTooltipHoverLeave}
        >
        <Autocomplete
            classes={{
                root: classes.root,
                clearIndicator: classes.clearIndicator,
                listbox: classes.listbox,
                option: classes.option
            }}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.address}
            options={options}
            autoComplete
            includeInputInList
            freeSolo
            value={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            onHighlightChange={setHighlightValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    classes={{
                        root: classes.text
                    }}
                    placeholder="Search for a known location"
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        ...params.InputProps,
                        color: 'primary',
                        disableUnderline: true,
                        classes: {
                            root: classes.input
                        },
                        endAdornment: (
                            <InputAdornment
                                position="end"
                            >
                                <AutocompleteSearchIcon />
                            </InputAdornment>
                        )
                    }}
                    onFocus={handleAutocompleteFocus}
                />
            )}
            onMouseEnter={handleTooltipHover}
            onMouseLeave={handleTooltipHoverLeave}
            renderOption={(option) => {
                return (
                    <Grid
                        className={classes.option}
                        container
                        alignItems="center"
                        onClick={goTo}
                    >
                        <Grid item xs>
                            <Typography variant="body2" color="textSecondary">
                                {option.address}
                            </Typography>
                        </Grid>
                    </Grid>
                );
            }}
        />
                        </Tooltip>
    );
};

const style = theme => ({
    root: {
        width: '100%'
    },
    text: {
        width: '100%',
        height: 45,
        backgroundColor: theme.palette.common.white,
        borderRadius: theme.constants.radius.md,
        boxSizing: 'border-box',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
    },
    icon: {
        color: theme.palette.grey.variant3,
        marginRight: theme.spacing(2)
    },
    input: {
        color: theme.palette.grey.variant3,
        padding: `0 ${theme.constants.spacing.lg}px`,
        height: '100%',
        width: '100%'
    },
    clearIndicator: {
        color: theme.palette.background.contrastText,
        marginRight: '5px'
    },
    listbox: {
        padding: 2
    },
    option: {
        padding: 2,
        height: '35px'
    },
    customWidth: {
        maxWidth: 500,
      }
});

const mapStateToProps = state => ({
    map: state[MAIN_MAP]
});

const mapDispatchToProps = dispatch => ({
    dispatchSetFitBounds: (bounds, options = {}) => dispatch(setFitBounds(bounds, options, MAIN_MAP)),
    dispatchSetMarkers: (markers) => dispatch(setMarkers(markers)),
    dispatchRemoveMarker: (id) => dispatch(removeMarker(id)),
    dispatchSetHighlightedFeatures: (highlights) => dispatch(setHighlightedFeatures(highlights))
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withStyles(style)
)(AutocompleteSearch);
